import ToneChip from './tone-chip';
import { getInitials } from '../utils';
import { SpotlightCardProps, Tone } from '../types';
import { FormattedMessage, useIntl } from 'react-intl';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { SPOTLIGHT_TICKET_PREVIEW_COUNT } from '../constants';
import { Box, Paper, Stack, alpha, Avatar, Skeleton, useTheme, Typography } from '@mui/material';

const SpotlightCard = ({ summary, variant, loading }: SpotlightCardProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const isSuccess = variant === 'success';
  const tone: Tone = isSuccess ? 'success' : 'error';
  const color = theme.palette[tone].main;
  const agent = summary?.agent;
  const tickets = summary?.tickets || [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderRadius: '8px',
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.35 : 0.24),
        bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.12 : 0.06),
      }}>
      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton width={140} height={32} />
          <Skeleton width="70%" height={48} />
          <Skeleton variant="rounded" height={96} sx={{ borderRadius: '8px' }} />
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <ToneChip
              tone={tone}
              icon={isSuccess ? <EmojiEventsIcon /> : <TrendingUpIcon />}
              label={
                isSuccess
                  ? intl.formatMessage({ id: 'freshdesk.topCloser.badge' })
                  : intl.formatMessage({ id: 'freshdesk.topBusy.badge' })
              }
            />
            <Typography variant="h5" sx={{ fontWeight: 900, color }}>
              {intl.formatNumber(summary?.ticketCount || 0)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 48, height: 48, bgcolor: color }}>{getInitials(agent?.name)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
                {agent?.name || intl.formatMessage({ id: 'freshdesk.notAvailable' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {agent?.email || intl.formatMessage({ id: 'freshdesk.notAvailable' })}
              </Typography>
              <Typography variant="caption" sx={{ color, fontWeight: 800 }}>
                {intl.formatMessage(
                  { id: isSuccess ? 'freshdesk.topCloser.ticketCount' : 'freshdesk.topBusy.ticketCount' },
                  { count: intl.formatNumber(summary?.ticketCount || 0) }
                )}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1}>
            {tickets.length ? (
              tickets.slice(0, SPOTLIGHT_TICKET_PREVIEW_COUNT).map((ticket) => (
                <Box
                  key={ticket.id}
                  component="a"
                  href={ticket.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'block',
                    px: 1.25,
                    py: 1,
                    border: '1px solid',
                    borderColor: alpha(color, 0.2),
                    borderRadius: '8px',
                    color: 'inherit',
                    textDecoration: 'none',
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.12)
                        : alpha(theme.palette.common.white, 0.65),
                  }}>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="freshdesk.ticketNumber" values={{ id: ticket.id }} />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {ticket.subject}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="freshdesk.emptyTickets" />
              </Typography>
            )}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default SpotlightCard;
